/**
 * @engine/input — Hold Interaction
 *
 * Triggers after holding for a duration.
 */

import type { InteractionType, ControlValue, ActionPhase, ActionContext, HoldParams } from '../types';
import { InputInteraction, InteractionState, registerInteraction } from './InputInteraction';

interface HoldState extends InteractionState {
  wasActive: boolean;
  holdStartTime: number;
}

/**
 * Hold interaction — triggers after holding for duration
 */
export class HoldInteraction extends InputInteraction {
  readonly type: InteractionType = 'hold';

  /** Time required to hold (seconds) */
  private readonly duration: number;

  /** Emit 'started' immediately on press? */
  private readonly startOnPress: boolean;

  constructor(params: Partial<HoldParams> = {}) {
    super(params as Record<string, unknown>);
    this.duration = params.duration ?? 0.4;
    this.startOnPress = params.startOnPress ?? false;
  }

  override createState(): HoldState {
    return {
      phase: 'waiting',
      startTime: 0,
      wasActive: false,
      holdStartTime: 0,
    };
  }

  process(
    state: InteractionState,
    value: ControlValue,
    time: number,
    _context: ActionContext
  ): ActionPhase {
    const holdState = state as HoldState;
    const isActive = this.isActive(value);
    const wasActive = holdState.wasActive;
    holdState.wasActive = isActive;

    // Just pressed
    if (isActive && !wasActive) {
      holdState.holdStartTime = time;
      state.startTime = time;

      if (this.startOnPress) {
        state.phase = 'started';
        return 'started';
      }

      state.phase = 'started';
      return 'started';
    }

    // Holding
    if (isActive && wasActive) {
      const holdDuration = time - holdState.holdStartTime;

      if (state.phase === 'started' && holdDuration >= this.duration) {
        state.phase = 'performed';
        return 'performed';
      }

      // Stay in current phase while holding
      return state.phase === 'performed' ? 'performed' : 'started';
    }

    // Released
    if (!isActive && wasActive) {
      const wasPerformed = state.phase === 'performed';
      state.phase = 'canceled';

      // Reset for next press
      setTimeout(() => {
        state.phase = 'waiting';
      }, 0);

      return wasPerformed ? 'canceled' : 'canceled';
    }

    return 'waiting';
  }

  override reset(state: InteractionState): void {
    super.reset(state);
    const holdState = state as HoldState;
    holdState.wasActive = false;
    holdState.holdStartTime = 0;
  }

  /**
   * Get hold progress (0..1)
   */
  getProgress(state: InteractionState, time: number): number {
    const holdState = state as HoldState;
    if (!holdState.wasActive || state.phase === 'waiting') return 0;
    if (state.phase === 'performed') return 1;

    const duration = time - holdState.holdStartTime;
    return Math.min(1, duration / this.duration);
  }
}

registerInteraction('hold', HoldInteraction);
